import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  LiveKitRoom,
  GridLayout,
  ParticipantTile,
  RoomAudioRenderer,
  useTracks,
  ControlBar,
  useParticipants,
  useRoomContext,
  useLocalParticipant,
} from "@livekit/components-react";
import "@livekit/components-styles";
import { Track, RemoteParticipant, ParticipantKind } from "livekit-client";
import { CallManagerData, DeviceInfoResponse } from "../services/types";
import { useTranslation } from "../i18n";
import "./VideoCallPage.css";

// Call timeout boundaries (seconds). Kept in sync with the server
// (entrioaiserver) and the Add/Edit Intercom Device modal (entrioaiportal).
const INTERCOM_TIMEOUT_MIN_SEC = 10;
const INTERCOM_TIMEOUT_MAX_SEC = 180;
const INTERCOM_TIMEOUT_DEFAULT_SEC = 60;

/**
 * Coerce whatever the server (or local navigation state) gave us into a
 * valid call-timeout in seconds — number, defaulted to 60, clamped to
 * the [10, 180] range.
 */
const resolveCallTimeoutSeconds = (raw: unknown): number => {
  const num = Number(raw);
  if (!Number.isFinite(num)) return INTERCOM_TIMEOUT_DEFAULT_SEC;
  if (num < INTERCOM_TIMEOUT_MIN_SEC) return INTERCOM_TIMEOUT_MIN_SEC;
  if (num > INTERCOM_TIMEOUT_MAX_SEC) return INTERCOM_TIMEOUT_MAX_SEC;
  return Math.floor(num);
};

/**
 * Decide whether a remote participant counts as a real human answer for the
 * purposes of the no-answer timer.
 *
 * Resident calls are typically routed through a SIP gateway. The SIP
 * participant joins the LiveKit room the moment dialing begins, and its
 * `sip.callStatus` attribute will eventually transition to `active` either
 * when a real human picks up OR when carrier voicemail picks up — the SIP
 * layer can't distinguish between the two. The user's reported behavior:
 * resident calls play voicemail audio without ever auto-hanging-up, while
 * reception (mobile-app push) calls correctly auto-hang-up on no answer.
 *
 * To make both flows consistent, SIP-bridge participants are NEVER
 * considered "answered" here. The timer keeps running and will fire after
 * the configured timeout regardless of whether the SIP leg goes to
 * voicemail, hits a busy signal, or rings forever. Only non-SIP remotes
 * (e.g. the resident/manager's mobile app participant, which only joins
 * after the user accepts the call) count as a real answer.
 */
const isParticipantAnswered = (participant: {
  isLocal?: boolean;
  kind?: ParticipantKind | number;
  identity?: string;
}): boolean => {
  if (participant.isLocal) return false;
  const isSipKind = participant.kind === ParticipantKind.SIP;
  const isSipIdentity = (participant.identity || "").startsWith("sip:");
  if (isSipKind || isSipIdentity) return false;
  return true;
};

interface VideoCallPageProps {}

interface LocationState {
  callData: CallManagerData;
  deviceInfo: DeviceInfoResponse;
  sessionToken: string;
  callType: "manager" | "resident";
  callTarget?: {
    name: string;
    room?: string;
  };
}

interface TrackInfo {
  sid: string;
  source: string;
  isMuted: boolean;
  isEnabled: boolean;
  isSubscribed?: boolean;
}

interface ParticipantInfo {
  identity: string;
  sid: string;
  audioTracks: TrackInfo[];
  videoTracks: TrackInfo[];
}

interface DebugInfo {
  localParticipant: {
    identity: string;
    audioTracks: TrackInfo[];
    videoTracks: TrackInfo[];
  } | null;
  remoteParticipants: ParticipantInfo[];
  connectionState: string;
  isConnected: boolean;
}

// 添加参与者离开提示组件
const ParticipantLeftModal = ({
  onClose,
}: {
  onClose: () => void;
}): React.JSX.Element => {
  const { t } = useTranslation();

  useEffect(() => {
    // 3秒后自动关闭
    const timer = setTimeout(() => {
      onClose();
    }, 3000);

    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="participant-left-modal">
      <div className="modal-content">
        <div className="modal-icon"></div>
        <h3>{t.videoCall.callEnded}</h3>
        <p>{t.videoCall.otherPartyHungUp}</p>
        <button onClick={onClose} className="modal-button">
          {t.videoCall.ok}
        </button>
      </div>
    </div>
  );
};

// "No-one answered" modal — shown after the call times out and the device
// has hung up. Auto-closes after 8s and surfaces the configured timeout.
const NoAnswerModal = ({
  onClose,
  timeoutSeconds,
}: {
  onClose: () => void;
  timeoutSeconds: number;
}): React.JSX.Element => {
  const [countdown, setCountdown] = useState(8);
  const { t, translate } = useTranslation();

  useEffect(() => {
    // Auto-close after 8 seconds
    const autoCloseTimer = setTimeout(() => {
      onClose();
    }, 8000);

    // Countdown
    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearTimeout(autoCloseTimer);
      clearInterval(countdownInterval);
    };
  }, [onClose]);

  return (
    <div className="participant-left-modal">
      <div className="modal-content">
        <div className="modal-icon"></div>
        <h3>{t.videoCall.noAnswer}</h3>
        <p>
          {translate(t.videoCall.noAnswerMessage, {
            seconds: timeoutSeconds,
          })}
        </p>
        <p className="countdown-text">
          {translate(t.videoCall.autoClosing, { count: countdown })}
        </p>
        <button onClick={onClose} className="modal-button">
          {t.videoCall.ok}
        </button>
      </div>
    </div>
  );
};

// 调试信息组件
const DebugInfo = (): React.JSX.Element => {
  const participants = useParticipants();
  const room = useRoomContext();
  const [debugInfo, setDebugInfo] = useState<DebugInfo>({
    localParticipant: null,
    remoteParticipants: [],
    connectionState: "未连接",
    isConnected: false,
  });

  useEffect(() => {
    const updateDebugInfo = () => {
      // 添加安全检查
      if (!room || !room.localParticipant) {
        setDebugInfo({
          connectionState: "未连接",
          isConnected: false,
          localParticipant: null,
          remoteParticipants: [],
        });
        return;
      }

      const info: DebugInfo = {
        localParticipant: {
          identity: room.localParticipant.identity,
          audioTracks: Array.from(
            room.localParticipant.trackPublications.values()
          )
            .filter((track) => track.kind === "audio")
            .map((track) => ({
              sid: track.trackSid,
              source: track.source,
              isMuted: track.isMuted,
              isEnabled: track.isEnabled,
            })),
          videoTracks: Array.from(
            room.localParticipant.trackPublications.values()
          )
            .filter((track) => track.kind === "video")
            .map((track) => ({
              sid: track.trackSid,
              source: track.source,
              isMuted: track.isMuted,
              isEnabled: track.isEnabled,
            })),
        },
        remoteParticipants: participants
          .filter((p): p is RemoteParticipant => !p.isLocal)
          .map((p: RemoteParticipant) => ({
            identity: p.identity,
            sid: p.sid,
            audioTracks: Array.from(p.trackPublications.values())
              .filter((track) => track.kind === "audio")
              .map((track) => ({
                sid: track.trackSid,
                source: track.source,
                isMuted: track.isMuted,
                isEnabled: track.isEnabled,
                isSubscribed: track.isSubscribed,
              })),
            videoTracks: Array.from(p.trackPublications.values())
              .filter((track) => track.kind === "video")
              .map((track) => ({
                sid: track.trackSid,
                source: track.source,
                isMuted: track.isMuted,
                isEnabled: track.isEnabled,
                isSubscribed: track.isSubscribed,
              })),
          })),
        connectionState: room.state,
        isConnected: room.state === "connected",
      };
      setDebugInfo(info);
    };

    updateDebugInfo();
    const interval = setInterval(updateDebugInfo, 2000); // 每2秒更新一次

    return () => clearInterval(interval);
  }, [participants, room]);

  return (
    <div
      style={{
        position: "absolute",
        top: "10px",
        left: "10px",
        zIndex: 1000,
        backgroundColor: "rgba(0,0,0,0.8)",
        color: "white",
        padding: "15px",
        borderRadius: "8px",
        fontSize: "12px",
        maxWidth: "400px",
        maxHeight: "300px",
        overflow: "auto",
      }}
    >
      <h4 style={{ margin: "0 0 10px 0" }}>🔍 调试信息</h4>
      <div style={{ marginBottom: "10px" }}>
        <strong>连接状态:</strong> {debugInfo.connectionState || "未知"}
      </div>

      {debugInfo.localParticipant ? (
        <div style={{ marginBottom: "10px" }}>
          <strong>本地参与者:</strong>
          <div style={{ marginLeft: "10px" }}>
            <div>身份: {debugInfo.localParticipant.identity}</div>
            <div>
              音频轨道: {debugInfo.localParticipant.audioTracks?.length || 0}
            </div>
            <div>
              视频轨道: {debugInfo.localParticipant.videoTracks?.length || 0}
            </div>
          </div>
        </div>
      ) : (
        <div style={{ marginBottom: "10px" }}>
          <strong>本地参与者:</strong> 未连接
        </div>
      )}

      <div style={{ marginBottom: "10px" }}>
        <strong>
          远端参与者 ({debugInfo.remoteParticipants?.length || 0}):
        </strong>
        {debugInfo.remoteParticipants?.map(
          (p: ParticipantInfo, index: number) => (
            <div key={index} style={{ marginLeft: "10px", marginTop: "5px" }}>
              <div>身份: {p.identity}</div>
              <div>音频轨道: {p.audioTracks?.length || 0}</div>
              <div>视频轨道: {p.videoTracks?.length || 0}</div>
              {p.audioTracks?.map((track: TrackInfo, trackIndex: number) => (
                <div
                  key={trackIndex}
                  style={{ marginLeft: "10px", fontSize: "11px" }}
                >
                  🎤 音频: {track.isSubscribed ? "✅已订阅" : "❌未订阅"}
                  {track.isMuted ? "🔇静音" : "🔊有声"}
                </div>
              ))}
              {p.videoTracks?.map((track: TrackInfo, trackIndex: number) => (
                <div
                  key={trackIndex}
                  style={{ marginLeft: "10px", fontSize: "11px" }}
                >
                  📹 视频: {track.isSubscribed ? "✅已订阅" : "❌未订阅"}
                  {track.isMuted ? "🔇关闭" : "📺开启"}
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
};

// 自定义控制栏组件
const CustomControlBar = ({
  onEndCall,
}: {
  onEndCall?: () => void;
}): React.JSX.Element => {
  const { localParticipant } = useLocalParticipant();
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const { t } = useTranslation();

  const toggleAudio = async () => {
    if (localParticipant) {
      if (isAudioEnabled) {
        await localParticipant.setMicrophoneEnabled(false);
        setIsAudioEnabled(false);
      } else {
        await localParticipant.setMicrophoneEnabled(true);
        setIsAudioEnabled(true);
      }
    }
  };

  const toggleVideo = async () => {
    if (localParticipant) {
      if (isVideoEnabled) {
        await localParticipant.setCameraEnabled(false);
        setIsVideoEnabled(false);
      } else {
        await localParticipant.setCameraEnabled(true);
        setIsVideoEnabled(true);
      }
    }
  };

  return (
    <div className="custom-control-bar">
      <button
        className={`control-button ${!isAudioEnabled ? "muted" : ""}`}
        onClick={toggleAudio}
        title={isAudioEnabled ? t.videoCall.mute : t.videoCall.unmute}
      >
        {isAudioEnabled ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
            <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
            <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
            <line
              x1="1"
              y1="1"
              x2="23"
              y2="23"
              stroke="currentColor"
              strokeWidth="2"
            />
          </svg>
        )}
      </button>

      <button
        className={`control-button ${!isVideoEnabled ? "video-off" : ""}`}
        onClick={toggleVideo}
        title={
          isVideoEnabled ? t.videoCall.turnOffVideo : t.videoCall.turnOnVideo
        }
      >
        {isVideoEnabled ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
            <line
              x1="1"
              y1="1"
              x2="23"
              y2="23"
              stroke="currentColor"
              strokeWidth="2"
            />
          </svg>
        )}
      </button>

      {/* 添加End Call按钮 */}
      {onEndCall && (
        <button
          className="control-button end-call-control"
          onClick={onEndCall}
          title={t.videoCall.endCall}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.7l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.1-.7-.28-.79-.73-1.68-1.36-2.66-1.85-.33-.16-.56-.51-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z" />
          </svg>
        </button>
      )}
    </div>
  );
};

// 内部组件，在LiveKitRoom上下文中使用hooks
const RoomContent = ({
  onParticipantLeft,
  onEndCall,
  onRemoteParticipantJoined,
}: {
  onParticipantLeft: () => void;
  onEndCall: () => void;
  onRemoteParticipantJoined: () => void;
}): React.JSX.Element => {
  const participants = useParticipants();
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false }
  );

  // Tracks whether at least one *answered* participant has ever been in the
  // room. SIP-bridge participants that are still dialing/ringing don't count
  // — see `isParticipantAnswered` for the full reasoning.
  const [hasHadAnsweredRemote, setHasHadAnsweredRemote] = useState(false);

  // 监听参与者变化
  useEffect(() => {
    const answeredRemotes = participants.filter(isParticipantAnswered);

    // 如果曾经有过远程参与者，现在没有了，说明对方挂断了
    if (hasHadAnsweredRemote && answeredRemotes.length === 0) {
      // 延迟一点时间确保状态更新完成
      setTimeout(() => {
        onParticipantLeft();
      }, 1000);
    }

    // 当出现首位"真正接听"的远端时，清除无人接听定时器
    if (answeredRemotes.length > 0) {
      setHasHadAnsweredRemote(true);
      onRemoteParticipantJoined();
    }
  }, [
    participants,
    hasHadAnsweredRemote,
    onParticipantLeft,
    onRemoteParticipantJoined,
  ]);

  return (
    <>
      {/* 视频网格 */}
      <GridLayout tracks={tracks} className="video-grid">
        {React.createElement(ParticipantTile as any)}
      </GridLayout>

      {/* 自定义控制栏 - 传递onEndCall */}
      <CustomControlBar onEndCall={onEndCall} />

      {/* 播放房间内所有音轨 */}
      {React.createElement(RoomAudioRenderer as any)}

      {/* 调试信息 - 已隐藏 */}
      {/* <DebugInfo /> */}
    </>
  );
};

const VideoCallPage = (): React.JSX.Element => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState;
  const { callData, deviceInfo, callType, callTarget } = state || {};
  const { t, translate } = useTranslation();

  // Resolve the device's configured call timeout (clamped to 10–180s; 60s default)
  const callTimeoutSeconds = resolveCallTimeoutSeconds(
    deviceInfo?.device?.intercomTimeoutSeconds
  );

  const [showParticipantLeftModal, setShowParticipantLeftModal] =
    useState(false);
  const [showNoAnswerModal, setShowNoAnswerModal] = useState(false);
  // When `hasHungUp` flips to true we stop rendering the LiveKitRoom, which
  // cleanly disconnects the local participant from the call (a true hang-up).
  const [hasHungUp, setHasHungUp] = useState(false);

  // Refs (instead of state) so timer access inside callbacks is always up to
  // date and so we don't trigger re-renders just to track a timeout handle.
  const noAnswerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Once a remote participant has joined we never want the no-answer timer
  // to fire again, even if it was somehow re-armed.
  const hasRemoteJoinedRef = useRef(false);

  const clearNoAnswerTimer = useCallback(() => {
    if (noAnswerTimerRef.current) {
      clearTimeout(noAnswerTimerRef.current);
      noAnswerTimerRef.current = null;
    }
  }, []);

  // Hang up locally because the timeout elapsed without anyone joining.
  // Cancels the timer, unmounts the LiveKitRoom (real disconnect), and
  // surfaces the "no one is answering, please try again later" modal.
  const hangUpDueToTimeout = useCallback(() => {
    clearNoAnswerTimer();
    setHasHungUp(true);
    setShowNoAnswerModal(true);
  }, [clearNoAnswerTimer]);

  const startNoAnswerTimer = useCallback(() => {
    clearNoAnswerTimer();
    noAnswerTimerRef.current = setTimeout(() => {
      noAnswerTimerRef.current = null;
      // Bail out if the call is already answered or already hung up.
      if (hasRemoteJoinedRef.current) return;
      hangUpDueToTimeout();
    }, callTimeoutSeconds * 1000);
  }, [callTimeoutSeconds, clearNoAnswerTimer, hangUpDueToTimeout]);

  const handleLeaveCall = useCallback(() => {
    clearNoAnswerTimer();
    navigate(-1);
  }, [clearNoAnswerTimer, navigate]);

  const handleParticipantLeft = useCallback(() => {
    clearNoAnswerTimer();
    setShowParticipantLeftModal(true);
  }, [clearNoAnswerTimer]);

  const handleRemoteParticipantJoined = useCallback(() => {
    hasRemoteJoinedRef.current = true;
    clearNoAnswerTimer();
  }, [clearNoAnswerTimer]);

  const handleModalClose = useCallback(() => {
    setShowParticipantLeftModal(false);
    navigate(-1);
  }, [navigate]);

  const handleNoAnswerModalClose = useCallback(() => {
    setShowNoAnswerModal(false);
    navigate(-1);
  }, [navigate]);

  // Start the timer on mount; tear it down on unmount.
  useEffect(() => {
    startNoAnswerTimer();
    return () => {
      clearNoAnswerTimer();
    };
  }, []);

  // 修改getCallTitle函数，显示设备名称
  const getCallTitle = () => {
    if (callType === "manager") {
      return t.videoCall.callingManager;
    } else if (callType === "resident" && callTarget) {
      return translate(t.videoCall.callingResident, { name: callTarget.name });
    }
    // 如果有设备信息，显示设备名称
    if (deviceInfo?.device?.name) {
      return deviceInfo.device.name;
    }
    return t.videoCall.videoCall;
  };

  const getCallSubtitle = () => {
    if (callType === "resident" && callTarget?.room) {
      return translate(t.videoCall.room, { room: callTarget.room });
    }
    return null;
  };

  if (!state || !callData) {
    return (
      <div className="video-call-page error">
        <div className="error-container">
          <h2>{t.videoCall.callError}</h2>
          <p>{t.videoCall.invalidCallData}</p>
          <button onClick={() => navigate(-1)} className="back-button">
            {t.videoCall.goBack}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="video-call-page">
      {/* 参与者离开提示模态框 */}
      {showParticipantLeftModal && (
        <ParticipantLeftModal onClose={handleModalClose} />
      )}

      {/* 无人接听提示模态框 */}
      {showNoAnswerModal && (
        <NoAnswerModal
          onClose={handleNoAnswerModalClose}
          timeoutSeconds={callTimeoutSeconds}
        />
      )}

      {/* 房间信息显示 */}
      <div className="call-header">
        <div className="call-info">
          <h2>{getCallTitle()}</h2>
          {getCallSubtitle() && (
            <div className="call-subtitle">{getCallSubtitle()}</div>
          )}
          {deviceInfo && (
            <div className="device-info">
              {deviceInfo.device.apartment.name} -{" "}
              {deviceInfo.device.building.name}
            </div>
          )}
        </div>

        {/* 离开按钮 */}
        <button onClick={handleLeaveCall} className="end-call-button">
          {t.videoCall.endCall}
        </button>
      </div>

      {/* LiveKit room — unmounted on hang-up, which actually disconnects */}
      {!hasHungUp &&
        React.createElement(LiveKitRoom as any, {
          token: callData.jwtToken,
          serverUrl: callData.hostUrl,
          connect: true,
          audio: true,
          video: true,
          "data-lk-theme": "default",
          style: {
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            flex: 1,
          },
          className: "lk-room-container",
          children: React.createElement(RoomContent, {
            onParticipantLeft: handleParticipantLeft,
            onEndCall: handleLeaveCall,
            onRemoteParticipantJoined: handleRemoteParticipantJoined,
          }),
        })}
    </div>
  );
};

export default VideoCallPage;
